import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Gamepad2, Gift, ArrowDownToLine, Search, Save,
  Ban, ShieldCheck, TrendingUp, Coins, Loader2, Check, X, Zap, Frown, RotateCcw,
  Megaphone, Trash2, Image as ImageIcon, Type, Upload, ScrollText, AlertTriangle,
  Headset, Send, ImagePlus, CreditCard, QrCode, ArrowUpFromLine, Monitor, MapPin, Clock,
  ShieldAlert, Repeat, LogIn, UserPlus, Trophy, Bell, BellOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatCoins, avatarUrl } from "../lib/format";
import { useLang } from "../context/LangContext";
import { playNewRound, isMuted, setMuted } from "../lib/sound";
import Confetti from "../components/Confetti";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { QRCodeCanvas } from "qrcode.react";
import { CopyId, FirstDepositCard, VipLevelsCard, AppRulesCard, ColorRoundStats, FestivalCard, DailySpinCard } from "../components/admin/ConfigExtras";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "games", label: "Games", icon: Gamepad2 },
  { id: "rewards", label: "Rewards", icon: Gift },
  { id: "content", label: "Content", icon: Megaphone },
  { id: "withdrawals", label: "Withdrawals", icon: ArrowDownToLine },
  { id: "payinfo", label: "Payments", icon: CreditCard },
  { id: "kyc", label: "KYC", icon: ShieldCheck },
  { id: "promos", label: "Promos", icon: Gift },
  { id: "fraud", label: "Fraud", icon: ShieldAlert },
  { id: "support", label: "Support", icon: Headset },
  { id: "activity", label: "Activity", icon: ScrollText },
];

const NumInput = ({ value, onChange, step = 1, min, cls = "", testid }) => (
  <input type="number" value={value} step={step} min={min} data-testid={testid}
    onChange={(e) => onChange(Number(e.target.value))}
    className={`rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1 font-mono-num text-amber-200 text-sm w-24 focus:outline-none focus:border-amber-500/80 ${cls}`} />
);

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck size={28} className="text-amber-400" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">Admin Panel</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((x) => (
          <button key={x.id} onClick={() => setTab(x.id)} data-testid={`admin-tab-${x.id}`}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-colors ${tab === x.id ? "btn-gold" : "border border-amber-500/25 bg-black/30 text-slate-300 hover:text-amber-200"}`}>
            <x.icon size={16} /> {x.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "users" && <UsersTab />}
      {tab === "games" && <GamesTab />}
      {tab === "rewards" && <RewardsTab />}
      {tab === "content" && <ContentTab />}
      {tab === "withdrawals" && <WithdrawalsTab />}
      {tab === "payinfo" && <PayInfoTab />}
      {tab === "kyc" && <KycTab />}
      {tab === "promos" && <PromoTab />}
      {tab === "fraud" && <FraudTab />}
      {tab === "support" && <SupportTab />}
      {tab === "activity" && <LogsTab />}
    </div>
  );
}

function MaintenanceToggle() {
  const { t } = useLang();
  const [m, setM] = useState({ enabled: false, message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/maintenance").then(({ data }) => setM({ enabled: !!data.enabled, message: data.message || "" })).catch(() => {}); }, []);
  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/maintenance", next);
      setM({ enabled: !!data.enabled, message: data.message || "" });
      toast.success(data.enabled ? t("maintenanceOn") : t("maintenanceOff"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className={`mw-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${m.enabled ? "border-rose-500/50" : ""}`} data-testid="admin-maintenance">
      <div className="flex items-center gap-3 flex-1">
        <div className={`grid place-items-center h-10 w-10 rounded-xl ${m.enabled ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>
          <AlertTriangle size={18} />
        </div>
        <div>
          <p className="font-display font-bold">{t("maintenanceMode")}</p>
          <p className={`text-xs ${m.enabled ? "text-rose-300" : "text-emerald-300"}`}>{m.enabled ? t("maintenanceOn") : t("maintenanceOff")}</p>
        </div>
      </div>
      <input value={m.message} onChange={(e) => setM({ ...m, message: e.target.value })} placeholder={t("maintenanceDefault")}
        data-testid="maintenance-message" className="flex-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
      <button onClick={() => save({ enabled: !m.enabled, message: m.message })} disabled={busy} data-testid="maintenance-toggle-btn"
        className={`rounded-xl px-5 py-2.5 text-sm font-bold flex items-center justify-center gap-2 ${m.enabled ? "border border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border border-rose-500/50 bg-rose-500/10 text-rose-300"}`}>
        {busy && <Loader2 size={15} className="animate-spin" />}
        {m.enabled ? t("turnOff") : t("turnOn")}
      </button>
    </div>
  );
}


function OnlinePlayers() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/online").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 12000); return () => clearInterval(id); }, []);
  const users = data?.users || [];
  return (
    <div className="mw-card p-4" data-testid="online-players">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          Online Now
        </h3>
        <span className="text-xs text-slate-400" data-testid="online-count">
          {data ? `${data.count} online · ${data.playing} playing` : "…"}
        </span>
      </div>
      {!data ? (
        <div className="py-5 grid place-items-center"><Loader2 size={18} className="animate-spin text-amber-400" /></div>
      ) : users.length === 0 ? (
        <p className="text-sm text-slate-500">Abhi koi player online nahi hai</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
          {users.map((u) => {
            const st = userStatus(u);
            return (
              <div key={u.user_id} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-black/30 p-2.5" data-testid={`online-user-${u.user_id}`}>
                <div className="relative shrink-0">
                  <div className="grid place-items-center h-8 w-8 rounded-full bg-gradient-to-br from-amber-400/30 to-purple-600/20 border border-white/10 text-xs font-bold overflow-hidden">
                    {u.picture ? <img src={avatarUrl(u.picture)} alt="" className="h-full w-full object-cover" /> : (u.name?.[0] || "?").toUpperCase()}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-[#12131d]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{u.name}</p>
                  <p className="flex items-center gap-1 font-mono-num text-[11px] text-cyan-300 min-w-0"><span className="truncate select-all" data-testid={`online-user-id-${u.user_id}`}>{u.user_id}</span><CopyId id={u.user_id} /></p>
                  <p className="text-[11px] truncate">
                    {st.playing
                      ? <span className="text-emerald-300">▶ {GAME_LABELS[st.game] || st.game}</span>
                      : <span className="text-slate-400">lobby me</span>}
                  </p>
                </div>
                <span className="font-mono-num text-xs font-bold text-amber-200 shrink-0">{formatCoins(u.coins)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const BIG_WIN = 1000;

function DailySummaryCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/daily-summary/latest").then(({ data }) => setS(data && data.date ? data : null)).catch(() => {}); }, []);
  if (!s) return null;
  const items = [
    { l: "Naye players", v: s.new_users, c: "text-sky-300" },
    { l: "Deposits", v: formatCoins(s.deposits), c: "text-emerald-300" },
    { l: "Withdrawals", v: formatCoins(s.withdrawals), c: "text-cyan-300" },
    { l: "House profit", v: formatCoins(s.house_profit), c: "text-amber-300" },
    { l: "Rounds", v: s.rounds, c: "text-fuchsia-300" },
    { l: "Fraud flags", v: s.fraud_flags, c: s.fraud_flags > 0 ? "text-rose-300" : "text-slate-300" },
  ];
  return (
    <div className="mw-card p-4" data-testid="daily-summary">
      <h3 className="font-display font-bold mb-3 flex items-center gap-2"><ScrollText size={16} className="text-amber-400" /> Daily Summary <span className="text-xs text-slate-500 font-normal">· {s.date}</span></h3>
      <div className="grid grid-cols-3 gap-2">
        {items.map((x, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-3 text-center">
            <p className="text-[10px] uppercase text-slate-400">{x.l}</p>
            <p className={`font-mono-num font-bold ${x.c}`}>{x.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveFeed() {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("all");
  const [newCount, setNewCount] = useState(0);
  const [muted, setMutedState] = useState(isMuted());
  const [confetti, setConfetti] = useState(0);
  const [bigWin, setBigWin] = useState(BIG_WIN);
  const [bwInput, setBwInput] = useState("");
  const [editBw, setEditBw] = useState(false);
  const lastTsRef = useRef(null);
  const initRef = useRef(false);
  const bigWinRef = useRef(BIG_WIN);

  useEffect(() => {
    api.get("/admin/feed-settings").then(({ data }) => {
      const v = Number(data.big_win) || BIG_WIN;
      setBigWin(v); setBwInput(String(v)); bigWinRef.current = v;
    }).catch(() => {});
  }, []);
  const saveBigWin = async () => {
    const v = Math.max(1, Number(bwInput) || BIG_WIN);
    try {
      const { data } = await api.put("/admin/feed-settings", { big_win: v });
      setBigWin(data.big_win); setBwInput(String(data.big_win)); bigWinRef.current = data.big_win;
      setEditBw(false); toast.success(`Big win ≥ ₹${data.big_win}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const load = () => api.get("/admin/feed").then(({ data }) => {
    const newest = data[0]?.ts || null;
    const th = bigWinRef.current;
    if (!initRef.current) {
      initRef.current = true;
      lastTsRef.current = newest;
    } else if (newest && newest !== lastTsRef.current) {
      const fresh = data.filter((e) => e.ts && (!lastTsRef.current || e.ts > lastTsRef.current));
      const bigWins = fresh.filter((e) => e.type === "game" && e.win && Math.abs(e.amount) >= th);
      const alertable = fresh.filter((e) => e.type === "deposit" || (e.type === "game" && e.win && Math.abs(e.amount) >= th));
      if (alertable.length > 0) {
        setNewCount((n) => n + alertable.length);
        if (!isMuted()) playNewRound();
      }
      if (bigWins.length > 0) setConfetti(Date.now());
      lastTsRef.current = newest;
    }
    setEvents(data);
  }).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, []);
  const toggleMute = () => { const m = !muted; setMuted(m); setMutedState(m); };

  const CHIPS = [
    { id: "all", label: "All" },
    { id: "win", label: "Big Wins" },
    { id: "deposit", label: "Deposits" },
    { id: "withdraw", label: "Withdrawals" },
    { id: "login", label: "Logins" },
    { id: "signup", label: "Signups" },
  ];
  const match = (e) => filter === "all" ? true : filter === "win" ? (e.type === "game" && e.win && Math.abs(e.amount) >= bigWin) : e.type === filter;
  const meta = {
    game: { icon: Gamepad2, color: "text-fuchsia-300", bg: "bg-fuchsia-500/15" },
    deposit: { icon: ArrowDownToLine, color: "text-emerald-300", bg: "bg-emerald-500/15" },
    withdraw: { icon: ArrowUpFromLine, color: "text-cyan-300", bg: "bg-cyan-500/15" },
    login: { icon: LogIn, color: "text-amber-300", bg: "bg-amber-500/15" },
    signup: { icon: UserPlus, color: "text-sky-300", bg: "bg-sky-500/15" },
  };
  const line = (e) => {
    if (e.type === "game") return <>{e.win ? <span className="text-emerald-300">won {formatCoins(Math.abs(e.amount))}</span> : <span className="text-rose-300">lost {formatCoins(Math.abs(e.amount))}</span>} · <span className="capitalize">{GAME_LABELS[e.game] || e.game}</span></>;
    if (e.type === "deposit") return <><span className="text-emerald-300">deposited {formatCoins(e.amount)}</span> <span className="text-slate-500">· {e.status}</span></>;
    if (e.type === "withdraw") return <><span className="text-cyan-300">withdrew {formatCoins(e.amount)}</span> <span className="text-slate-500">· {e.status}</span></>;
    if (e.type === "login") return <>logged in <span className="text-slate-500">{e.meta ? `· ${e.meta}` : ""}</span></>;
    if (e.type === "signup") return <span className="text-sky-300">naya account bana</span>;
    return "";
  };
  return (
    <div className="mw-card p-4" data-testid="live-feed">
      <Confetti fire={confetti} />
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold flex items-center gap-2">
          <Trophy size={16} className="text-amber-400" /> Live Activity
          {newCount > 0 && (
            <button onClick={() => setNewCount(0)} data-testid="feed-new-badge" className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-black animate-pulse">{newCount} new</button>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={toggleMute} data-testid="feed-mute" title={muted ? "Unmute" : "Mute"} className="h-7 w-7 grid place-items-center rounded-full border border-white/10 text-slate-400 hover:text-amber-200">
            {muted ? <BellOff size={13} /> : <Bell size={13} />}
          </button>
          <span className="text-[10px] uppercase tracking-wider text-emerald-300 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> live</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="feed-filters">
        {CHIPS.map((c) => (
          <button key={c.id} onClick={() => setFilter(c.id)} data-testid={`feed-chip-${c.id}`}
            className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${filter === c.id ? "btn-gold" : "border border-white/10 bg-black/30 text-slate-300 hover:text-amber-200"}`}>
            {c.label}
          </button>
        ))}
        <span className="flex-1" />
        {editBw ? (
          <span className="flex items-center gap-1" data-testid="bigwin-edit">
            <span className="text-[11px] text-slate-400">Big win ≥ ₹</span>
            <input type="number" min={1} value={bwInput} onChange={(e) => setBwInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveBigWin()}
              data-testid="bigwin-input" className="w-20 rounded-lg border border-amber-500/40 bg-black/40 px-2 py-0.5 text-[11px] font-mono-num text-amber-200 focus:outline-none" autoFocus />
            <button onClick={saveBigWin} data-testid="bigwin-save" className="rounded-lg btn-gold px-2 py-0.5 text-[10px] font-bold">Save</button>
          </span>
        ) : (
          <button onClick={() => setEditBw(true)} data-testid="bigwin-toggle"
            className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2.5 py-1 text-[11px] font-bold text-amber-300 hover:bg-amber-500/15">
            🏆 Big win ≥ ₹{bigWin}
          </button>
        )}
      </div>
      {!events ? (
        <div className="py-5 grid place-items-center"><Loader2 size={18} className="animate-spin text-amber-400" /></div>
      ) : (() => {
        const list = events.filter(match);
        return list.length === 0 ? (
          <p className="text-sm text-slate-500">Is filter me abhi koi activity nahi</p>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-white/5" data-testid="feed-list">
            {list.map((e, i) => {
              const m = meta[e.type] || meta.game;
              const isBig = e.type === "game" && e.win && Math.abs(e.amount) >= bigWin;
              if (isBig) {
                return (
                  <div key={i} className="my-1.5 -mx-1 flex items-center gap-3 rounded-xl border border-amber-400/50 bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-transparent px-3 py-2.5 shadow-[0_0_18px_rgba(255,200,0,0.25)]" data-testid="feed-bigwin">
                    <div className="shrink-0 grid place-items-center h-8 w-8 rounded-lg bg-amber-400/25 text-amber-200"><Trophy size={16} /></div>
                    <p className="text-sm min-w-0 flex-1 truncate">
                      <span className="font-bold gold-text">{e.user}</span>{e.user_id && <span className="font-mono-num text-[11px] text-cyan-300"> ({e.user_id})</span>}{" "}
                      <span className="text-amber-200">BIG WIN {formatCoins(Math.abs(e.amount))}</span>{" "}
                      <span className="text-amber-100/70 capitalize">· {GAME_LABELS[e.game] || e.game}</span>
                    </p>
                    <span className="shrink-0 rounded-full bg-amber-400 px-2 py-0.5 text-[9px] font-extrabold text-black">🏆 BIG</span>
                  </div>
                );
              }
              return (
                <div key={i} className="py-2 flex items-center gap-3">
                  <div className={`shrink-0 grid place-items-center h-7 w-7 rounded-lg ${m.bg} ${m.color}`}><m.icon size={14} /></div>
                  <p className="text-sm min-w-0 flex-1 truncate"><span className="font-semibold">{e.user}</span>{e.user_id && <span className="font-mono-num text-[11px] text-cyan-300"> ({e.user_id})</span>} <span className="text-slate-300">{line(e)}</span></p>
                  <span className="text-[10px] text-slate-500 shrink-0">{e.ts ? timeAgo(e.ts) : ""}</span>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}


function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <Loader2 className="animate-spin text-amber-400" />;
  const cards = [
    { label: "Total Players", value: s.total_users, icon: Users, color: "#00F0FF" },
    { label: "Total Rounds", value: s.total_rounds, icon: Gamepad2, color: "#9D4EDD" },
    { label: "House Profit", value: formatCoins(s.house_profit), icon: TrendingUp, color: s.house_profit >= 0 ? "#00E676" : "#FF3366" },
    { label: "Balance in Circulation", value: formatCoins(s.coins_circulation), icon: Coins, color: "#FFD700" },
    { label: "Total Wagered", value: formatCoins(s.wagered), icon: Coins, color: "#FFD700" },
    { label: "Banned", value: s.banned, icon: Ban, color: "#FF3366" },
  ];
  return (
    <div className="space-y-6">
      <MaintenanceToggle />
      <OnlinePlayers />
      <DailySummaryCard />
      <LiveFeed />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="mw-card p-4" data-testid={`stat-${c.label}`}>
            <c.icon size={20} style={{ color: c.color }} />
            <p className="text-xs uppercase tracking-wider text-slate-400 mt-2">{c.label}</p>
            <p className="font-mono-num text-2xl font-bold mt-0.5" style={{ color: c.color }}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="mw-card p-4">
        <h3 className="font-display font-bold mb-3">Per-Game Breakdown</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(s.by_game).map(([g, d]) => (
            <div key={g} className="rounded-lg border border-white/10 bg-black/30 p-3">
              <p className="font-bold capitalize">{g}</p>
              <p className="text-xs text-slate-400 mt-1">Rounds: <span className="font-mono-num text-slate-200">{d.rounds}</span></p>
              <p className="text-xs text-slate-400">Profit: <span className="font-mono-num" style={{ color: d.wagered - d.won >= 0 ? "#00E676" : "#FF3366" }}>{formatCoins(d.wagered - d.won)}</span></p>
            </div>
          ))}
        </div>
      </div>

      <DailyReports />
    </div>
  );
}

function DailyReports() {
  const { t } = useLang();
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/admin/stats/daily?days=14").then(({ data }) => setData(data)).catch(() => {}); }, []);
  const fmt = (d) => d.slice(5);
  const tip = { background: "#12131d", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 12, fontSize: 12 };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-reports">
      <div className="mw-card p-4">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-emerald-400" /> {t("depositsLabel")} vs {t("withdrawalsLabel")} (14d)</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" tickFormatter={fmt} tick={{ fill: "#94a3b8", fontSize: 10 }} />
            <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
            <Tooltip contentStyle={tip} labelStyle={{ color: "#e2e8f0" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="deposits" name={t("depositsLabel")} fill="#00E676" radius={[4, 4, 0, 0]} />
            <Bar dataKey="withdrawals" name={t("withdrawalsLabel")} fill="#00F0FF" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mw-card p-4">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Coins size={18} className="text-amber-400" /> {t("houseProfitLabel")} (14d)</h3>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFD700" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#FFD700" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" tickFormatter={fmt} tick={{ fill: "#94a3b8", fontSize: 10 }} />
            <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
            <Tooltip contentStyle={tip} labelStyle={{ color: "#e2e8f0" }} />
            <Area type="monotone" dataKey="profit" name={t("houseProfitLabel")} stroke="#FFD700" strokeWidth={2} fill="url(#profitGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const GAME_LABELS = {
  spin: "Spin Wheel", color: "Color Prediction", dice: "Dice", scratch: "Scratch Card",
  aviator: "Aviator", teenpatti: "Teen Patti", andarbahar: "Andar Bahar", dragontiger: "Dragon Tiger",
};
const ONLINE_MS = 2 * 60 * 1000;

function userStatus(u) {
  const now = Date.now();
  const seen = u.last_seen ? new Date(u.last_seen).getTime() : 0;
  const gameAt = u.last_game_at ? new Date(u.last_game_at).getTime() : 0;
  const online = !!seen && now - seen < ONLINE_MS;
  const playing = online && !!gameAt && now - gameAt < ONLINE_MS;
  return { online, playing, game: u.last_game, seen };
}

function timeAgo(ts) {
  if (!ts) return "never";
  const m = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function StatusBadge({ u }) {
  const s = userStatus(u);
  if (s.playing) return (
    <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-300" data-testid={`status-${u.user_id}`}>
      <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> {(GAME_LABELS[s.game] || s.game || "Playing")}
    </span>
  );
  if (s.online) return (
    <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-300" data-testid={`status-${u.user_id}`}>
      <span className="h-2 w-2 rounded-full bg-emerald-400" /> Online
    </span>
  );
  return (
    <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-2 py-0.5 text-[11px] font-semibold text-slate-400" data-testid={`status-${u.user_id}`}>
      <span className="h-2 w-2 rounded-full bg-slate-600" /> {u.last_seen ? timeAgo(u.last_seen) : "Offline"}
    </span>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null); // selected user_id for control modal
  const [checked, setChecked] = useState([]); // user_ids selected for bulk
  const [bulkOpen, setBulkOpen] = useState(false);
  const [schedRefresh, setSchedRefresh] = useState(0);

  const load = () => api.get(`/admin/users?search=${encodeURIComponent(search)}`).then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  // auto-refresh for live online/playing status
  useEffect(() => { const id = setInterval(load, 20000); return () => clearInterval(id); }, [search]);

  const online = users.filter((u) => userStatus(u).online).length;
  const selUser = users.find((u) => u.user_id === sel) || null;
  const toggle = (id) => setChecked((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);
  const allChecked = users.length > 0 && checked.length === users.length;
  const toggleAll = () => setChecked(allChecked ? [] : users.map((u) => u.user_id));

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="flex items-center gap-2 flex-1 rounded-xl border border-amber-500/25 bg-black/30 px-3">
          <Search size={16} className="text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name or email..." data-testid="admin-user-search"
            className="w-full bg-transparent py-2 text-sm focus:outline-none" />
        </div>
        <button onClick={load} data-testid="admin-user-search-btn" className="btn-gold rounded-xl px-4 text-sm">Search</button>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-400 px-1">
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="users-select-all">
          <input type="checkbox" checked={allChecked} onChange={toggleAll} className="accent-amber-400 h-4 w-4" />
          <span data-testid="users-count">{checked.length > 0 ? `${checked.length} selected` : `${users.length} players`}</span>
        </label>
        <span className="flex items-center gap-1.5 text-emerald-300" data-testid="users-online-count">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> {online} online now
        </span>
      </div>

      {checked.length > 0 && (
        <div className="mw-card border-amber-500/40 p-3 flex flex-wrap items-center gap-2" data-testid="bulk-action-bar">
          <span className="text-sm font-bold text-amber-200">{checked.length} players selected</span>
          <span className="flex-1" />
          <button onClick={() => setBulkOpen(true)} data-testid="bulk-open-btn" className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300">
            <Send size={13} /> Message / Bonus
          </button>
          <button onClick={() => setChecked([])} data-testid="bulk-clear-btn" className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-bold text-slate-300">Clear</button>
        </div>
      )}

      <ScheduledBonusList refresh={schedRefresh} />

      <div className="space-y-2">
        {users.map((u) => {
          const st = userStatus(u);
          const isChecked = checked.includes(u.user_id);
          return (
            <div key={u.user_id} data-testid={`admin-user-${u.user_id}`}
              className={`mw-card w-full p-4 transition-colors hover:border-amber-500/40 ${isChecked ? "border-amber-500/50" : ""}`}>
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={isChecked} onChange={() => toggle(u.user_id)} onClick={(e) => e.stopPropagation()}
                  data-testid={`user-check-${u.user_id}`} className="accent-amber-400 h-4 w-4 shrink-0 cursor-pointer" />
                <button onClick={() => setSel(u.user_id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`user-open-${u.user_id}`}>
                  <div className="relative shrink-0">
                    <div className="grid place-items-center h-10 w-10 rounded-full bg-gradient-to-br from-amber-400/30 to-purple-600/20 border border-white/10 font-bold overflow-hidden">
                      {u.picture ? <img src={avatarUrl(u.picture)} alt="" className="h-full w-full object-cover" /> : (u.name?.[0] || "?").toUpperCase()}
                    </div>
                    {st.online && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 border-2 border-[#12131d]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate flex items-center gap-2" data-testid={`user-name-${u.user_id}`}>
                      {u.name}
                      {u.banned && <span className="text-[11px] text-rose-400">(banned)</span>}
                      {u.force_next && <span className="text-[11px] text-amber-400">[force {u.force_next}]</span>}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    <p className="flex items-center gap-1 font-mono-num text-[11px] text-cyan-300 min-w-0"><span className="truncate">{u.user_id}</span><CopyId id={u.user_id} /></p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="flex items-center gap-1 font-mono-num font-bold text-amber-200"><Coins size={14} className="text-amber-400" />{formatCoins(u.coins)}</span>
                    <StatusBadge u={u} />
                  </div>
                </button>
              </div>
            </div>
          );
        })}
        {users.length === 0 && <p className="text-slate-500 text-sm">No users</p>}
      </div>

      {selUser && <UserControlModal user={selUser} onClose={() => setSel(null)} onChanged={load} />}
      {bulkOpen && <BulkActionModal ids={checked} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); setChecked([]); setSchedRefresh((n) => n + 1); load(); }} />}
    </div>
  );
}

function ScheduledBonusList({ refresh }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/scheduled-bonus").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [refresh]);
  const pending = items.filter((i) => i.status === "pending");
  if (pending.length === 0) return null;
  const cancel = async (id) => {
    try { await api.delete(`/admin/scheduled-bonus/${id}`); toast.success("Cancelled"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="mw-card border-amber-500/30 p-3 space-y-2" data-testid="scheduled-bonus-list">
      <p className="text-xs font-bold text-amber-200 flex items-center gap-1.5"><Clock size={13} /> Scheduled bonuses ({pending.length})</p>
      {pending.map((s) => (
        <div key={s.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 p-2.5 text-xs" data-testid={`sched-${s.id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-200">
              {s.amount ? <span className="text-emerald-300">₹{s.amount} bonus</span> : <span className="text-cyan-300">message</span>} · {s.user_ids.length} players
            </p>
            <p className="text-[11px] text-slate-400">{new Date(s.scheduled_at).toLocaleString()}{s.message ? ` · "${s.message.slice(0, 40)}"` : ""}</p>
          </div>
          <button onClick={() => cancel(s.id)} data-testid={`sched-cancel-${s.id}`} className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 font-bold text-rose-300">Cancel</button>
        </div>
      ))}
    </div>
  );
}

function BulkActionModal({ ids, onClose, onDone }) {
  const [mode, setMode] = useState("notify"); // notify | bonus
  const [amount, setAmount] = useState(50);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [when, setWhen] = useState("now"); // now | later
  const [schedAt, setSchedAt] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (when === "later") {
        if (!schedAt) { toast.error("Schedule time choose karein"); setBusy(false); return; }
        const iso = new Date(schedAt).toISOString();
        const { data } = await api.post("/admin/scheduled-bonus", {
          user_ids: ids, amount: mode === "bonus" ? Number(amount) : 0, title, message, scheduled_at: iso,
        });
        toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString()}`);
        onDone();
        return;
      }
      const body = { user_ids: ids, action: mode, title, message };
      if (mode === "bonus") body.amount = Number(amount);
      const { data } = await api.post("/admin/users/bulk", body);
      toast.success(mode === "bonus" ? `₹${data.amount} → ${data.affected} players` : `Sent to ${data.affected} players`);
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-4" onClick={onClose} data-testid="bulk-modal">
      <div className="mw-card w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <p className="font-display font-bold">Bulk action · {ids.length} players</p>
          <button onClick={onClose} data-testid="bulk-close" className="h-8 w-8 grid place-items-center rounded-full border border-white/10 text-slate-400 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setMode("notify")} data-testid="bulk-mode-notify" className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold ${mode === "notify" ? "btn-gold" : "border border-white/10 bg-black/30 text-slate-300"}`}>Send Message</button>
            <button onClick={() => setMode("bonus")} data-testid="bulk-mode-bonus" className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold ${mode === "bonus" ? "btn-gold" : "border border-white/10 bg-black/30 text-slate-300"}`}>Add Bonus ₹</button>
          </div>
          {mode === "bonus" && (
            <label className="block text-xs text-slate-400">Bonus amount (₹) — use negative to deduct
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bulk-amount"
                className="mt-1 w-full rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 font-mono-num text-amber-200 focus:outline-none" />
            </label>
          )}
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="bulk-title"
            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm focus:outline-none" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            placeholder={mode === "bonus" ? "Message (optional — auto if blank)" : "Message to send..."} data-testid="bulk-message"
            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm focus:outline-none resize-none" />

          <div className="flex gap-2">
            <button onClick={() => setWhen("now")} data-testid="bulk-when-now" className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold ${when === "now" ? "border border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border border-white/10 bg-black/30 text-slate-300"}`}>Abhi bhejo</button>
            <button onClick={() => setWhen("later")} data-testid="bulk-when-later" className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold flex items-center justify-center gap-1 ${when === "later" ? "border border-amber-500/50 bg-amber-500/10 text-amber-300" : "border border-white/10 bg-black/30 text-slate-300"}`}><Clock size={12} /> Schedule</button>
          </div>
          {when === "later" && (
            <label className="block text-xs text-slate-400">Kab bhejna hai
              <input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} data-testid="bulk-sched-at"
                className="mt-1 w-full rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm text-amber-200 focus:outline-none" />
              <span className="text-[10px] text-slate-500">Scheduled bonus har 15 min me check hota hai, to set samay ke 15 min ke andar chala jayega.</span>
            </label>
          )}

          <button onClick={submit} disabled={busy} data-testid="bulk-submit"
            className="btn-gold w-full rounded-xl py-2.5 text-sm font-bold flex items-center justify-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : (when === "later" ? <Clock size={15} /> : <Send size={15} />)}
            {when === "later"
              ? `Schedule for ${ids.length} players`
              : mode === "bonus" ? `Give bonus to ${ids.length}` : `Send to ${ids.length} players`}
          </button>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-white/10 bg-black/20 p-4">
    <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-2.5 font-bold">{title}</p>
    {children}
  </div>
);

function UserControlModal({ user, onClose, onChanged }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [logins, setLogins] = useState(null);
  const [amount, setAmount] = useState(1000);
  const [note, setNote] = useState("");
  const [win, setWin] = useState(user.win_rate ?? "");
  const [maxBet, setMaxBet] = useState(user.max_bet ?? "");
  const [dwl, setDwl] = useState(user.daily_withdraw_limit ?? "");
  const [name, setName] = useState(user.name || "");
  const [busy, setBusy] = useState(false);
  const uid = user.user_id;

  const loadActivity = () => api.get(`/admin/users/${uid}/activity`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { loadActivity(); }, [uid]);
  useEffect(() => { api.get(`/admin/users/${uid}/logins`).then(({ data }) => setLogins(data)).catch(() => setLogins([])); }, [uid]);

  const after = (msg) => { if (msg) toast.success(msg); onChanged(); loadActivity(); };
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const doCoins = async (mode) => {
    try { const { data } = await api.post(`/admin/users/${uid}/coins`, { mode, amount: Number(amount), note }); after(`Balance: ${formatCoins(data.coins)}`); setNote(""); }
    catch (e) { err(e); }
  };
  const doBan = async (banned) => { try { await api.post(`/admin/users/${uid}/ban`, { banned }); after(banned ? "Banned" : "Unbanned"); } catch (e) { err(e); } };
  const doForce = async (value) => { try { await api.post(`/admin/users/${uid}/force`, { value }); after(`Next outcome: ${value}`); } catch (e) { err(e); } };
  const doWin = async (rate) => { try { await api.post(`/admin/users/${uid}/winrate`, { rate }); after(rate == null ? "Win% cleared" : `Win ${rate}%`); } catch (e) { err(e); } };
  const doLimits = async () => { try { await api.post(`/admin/users/${uid}/limits`, { max_bet: maxBet ? Number(maxBet) : null, daily_withdraw_limit: dwl ? Number(dwl) : null }); after("Limits saved"); } catch (e) { err(e); } };
  const doRename = async () => { try { await api.post(`/admin/users/${uid}/profile`, { name }); after("Renamed"); } catch (e) { err(e); } };
  const doKyc = async (action) => { try { await api.post(`/admin/kyc/${uid}/action`, { action, note: "" }); after(`KYC ${action}d`); } catch (e) { err(e); } };
  const doDelete = async () => {
    if (!window.confirm(`Permanently delete ${user.name}? Saari details, history aur balance delete ho jayega. Ye wapas nahi aayega.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/users/${uid}`); toast.success("User deleted"); onChanged(); onClose(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  const st = userStatus(user);
  const kyc = data?.user?.kyc_status || (user.kyc || {}).status || "none";
  const kycColor = kyc === "approved" ? "text-emerald-400" : kyc === "pending" ? "text-amber-400" : kyc === "rejected" ? "text-rose-400" : "text-slate-500";
  const s = data?.stats || {};
  const Ic = { deposit: ArrowDownToLine, withdraw: ArrowUpFromLine, game: Gamepad2 };
  const stColor = (x) => x === "approved" || x === "win" ? "text-emerald-400" : x === "pending" ? "text-amber-400" : x === "rejected" || x === "loss" ? "text-rose-400" : "text-slate-400";

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-3 sm:p-4" onClick={onClose} data-testid="user-control-modal">
      <div className="mw-card w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="grid place-items-center h-11 w-11 rounded-full bg-gradient-to-br from-amber-400/30 to-purple-600/20 border border-white/10 font-bold overflow-hidden shrink-0">
            {user.picture ? <img src={avatarUrl(user.picture)} alt="" className="h-full w-full object-cover" /> : (user.name?.[0] || "?").toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display font-bold truncate flex items-center gap-2">{user.name} {user.banned && <span className="text-xs text-rose-400">(banned)</span>}</p>
            <p className="text-xs text-slate-400 truncate">{user.email}</p>
            <p className="flex items-center gap-1 font-mono-num text-[11px] text-cyan-300" data-testid="control-user-id">{user.user_id}<CopyId id={user.user_id} /></p>
          </div>
          <StatusBadge u={user} />
          <button onClick={onClose} data-testid="user-control-close" className="h-8 w-8 grid place-items-center rounded-full border border-white/10 text-slate-400 hover:text-white shrink-0"><X size={16} /></button>
        </div>

        <div className="overflow-y-auto p-4 space-y-3">
          {/* presence + balance */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="user-presence">
            <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-center">
              <p className="text-[10px] uppercase text-slate-400">Balance</p>
              <p className="font-mono-num font-bold text-amber-300">{formatCoins(data?.user?.coins ?? user.coins)}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-center">
              <p className="text-[10px] uppercase text-slate-400">Status</p>
              <p className={`font-bold text-sm ${st.online ? "text-emerald-300" : "text-slate-400"}`}>{st.playing ? (GAME_LABELS[st.game] || "Playing") : st.online ? "Online" : "Offline"}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-center">
              <p className="text-[10px] uppercase text-slate-400">Last seen</p>
              <p className="font-semibold text-sm text-slate-200">{timeAgo(user.last_seen)}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-center">
              <p className="text-[10px] uppercase text-slate-400">Last login</p>
              <p className="font-semibold text-sm text-slate-200">{user.last_login ? timeAgo(user.last_login) : "never"}</p>
            </div>
          </div>

          {/* stats */}
          <div className="grid grid-cols-3 gap-2" data-testid="user-stats">
            {[
              { l: t("depositsLabel"), v: s.deposited, c: "text-emerald-300" },
              { l: t("withdrawalsLabel"), v: s.withdrawn, c: "text-cyan-300" },
              { l: "Wagered", v: s.wagered, c: "text-slate-200" },
              { l: "Won", v: s.won, c: "text-emerald-300" },
              { l: "Biggest Win", v: s.biggest_win, c: "text-fuchsia-300" },
              { l: "Rounds", v: s.rounds, c: "text-slate-200", plain: true },
            ].map((x, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-2.5 text-center">
                <p className="text-[10px] uppercase text-slate-400">{x.l}</p>
                <p className={`font-mono-num font-bold ${x.c}`}>{x.plain ? (x.v || 0) : formatCoins(x.v || 0)}</p>
              </div>
            ))}
          </div>

          {/* Balance control */}
          <Section title="Balance (₹)">
            <div className="flex flex-wrap items-center gap-2">
              <NumInput value={amount} onChange={setAmount} testid={`coins-amount-${uid}`} />
              <button onClick={() => doCoins("add")} data-testid={`coins-add-${uid}`} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300">+ Add</button>
              <button onClick={() => doCoins("set")} data-testid={`coins-set-${uid}`} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-bold text-cyan-300">Set</button>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("note")} data-testid={`coins-note-${uid}`} className="flex-1 min-w-[120px] rounded-lg border border-amber-500/25 bg-black/30 px-2 py-1.5 text-xs focus:outline-none" />
            </div>
          </Section>

          {/* Win control */}
          <Section title="Winning control">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => doForce("win")} data-testid={`force-win-${uid}`} className="flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300"><Zap size={12} /> Force Win</button>
              <button onClick={() => doForce("lose")} data-testid={`force-lose-${uid}`} className="flex items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-bold text-rose-300"><Frown size={12} /> Force Lose</button>
              <button onClick={() => doForce("none")} data-testid={`force-clear-${uid}`} className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-bold text-slate-300">Clear</button>
              <span className="w-px h-5 bg-white/10" />
              <span className="text-[11px] text-slate-400">{t("winRate")} %</span>
              <input type="number" min={0} max={100} value={win} onChange={(e) => setWin(e.target.value)} placeholder="auto" data-testid={`winrate-input-${uid}`} className="w-16 rounded-lg border border-fuchsia-500/40 bg-black/30 px-2 py-1.5 text-xs text-fuchsia-200 focus:outline-none" />
              <button onClick={() => doWin(win === "" ? null : Math.max(0, Math.min(100, Number(win))))} data-testid={`winrate-set-${uid}`} className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-2 py-1.5 text-xs font-bold text-fuchsia-300">Set</button>
              <button onClick={() => { setWin(""); doWin(null); }} data-testid={`winrate-clear-${uid}`} className="rounded-lg border border-slate-600 px-2 py-1.5 text-xs text-slate-300">×</button>
            </div>
          </Section>

          {/* Limits + rename */}
          <Section title="Limits & profile">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400">{t("maxBet")}</span>
              <input type="number" min={0} value={maxBet} onChange={(e) => setMaxBet(e.target.value)} placeholder="∞" data-testid={`maxbet-input-${uid}`} className="w-20 rounded-lg border border-amber-500/30 bg-black/30 px-2 py-1.5 text-xs focus:outline-none" />
              <span className="text-[11px] text-slate-400">{t("dailyWithdrawLimit")}</span>
              <input type="number" min={0} value={dwl} onChange={(e) => setDwl(e.target.value)} placeholder="default" data-testid={`dwl-input-${uid}`} className="w-24 rounded-lg border border-amber-500/30 bg-black/30 px-2 py-1.5 text-xs focus:outline-none" />
              <button onClick={doLimits} data-testid={`limits-save-${uid}`} className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-300">Save</button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("rename")} data-testid={`rename-input-${uid}`} className="flex-1 min-w-[140px] rounded-lg border border-cyan-500/30 bg-black/30 px-2 py-1.5 text-xs focus:outline-none" />
              <button onClick={doRename} data-testid={`rename-save-${uid}`} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-bold text-cyan-300">{t("rename")}</button>
            </div>
          </Section>

          {/* KYC + ban */}
          <Section title="KYC & access">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400">KYC:</span>
              <span className={`text-xs font-bold ${kycColor}`}>{kyc}</span>
              <button onClick={() => doKyc("approve")} data-testid={`kyc-approve-${uid}`} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300">Approve</button>
              <button onClick={() => doKyc("reject")} data-testid={`kyc-reject-${uid}`} className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-bold text-rose-300">Reject</button>
              <span className="w-px h-5 bg-white/10" />
              {user.banned
                ? <button onClick={() => doBan(false)} data-testid={`unban-${uid}`} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300">Unban</button>
                : <button onClick={() => doBan(true)} data-testid={`ban-${uid}`} className="flex items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-bold text-rose-300"><Ban size={12} /> Ban</button>}
            </div>
          </Section>

          {/* Login history */}
          <Section title="Login history (device & location)">
            {!logins ? <div className="py-5 grid place-items-center"><Loader2 size={18} className="animate-spin text-amber-400" /></div> : (
              logins.length === 0 ? <p className="text-sm text-slate-500">No logins recorded yet</p> : (
                <div className="max-h-48 overflow-y-auto divide-y divide-white/5" data-testid="login-history">
                  {logins.map((l, i) => (
                    <div key={i} className="py-2 flex items-start gap-3">
                      <Monitor size={15} className="text-cyan-300 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{l.device || "Unknown device"} <span className="text-[10px] text-slate-500 font-normal">· {l.method === "google" ? "Google" : "Password"}</span></p>
                        <p className="text-[11px] text-slate-400 flex flex-wrap items-center gap-x-2">
                          {l.location && <span className="flex items-center gap-1"><MapPin size={11} />{l.location}</span>}
                          {l.ip && <span className="font-mono-num">{l.ip}</span>}
                        </p>
                        <p className="text-[10px] text-slate-500 flex items-center gap-1"><Clock size={10} />{l.created_at ? new Date(l.created_at).toLocaleString() : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </Section>

          {/* Activity timeline */}
          <Section title="Activity timeline">
            {!data ? <div className="py-6 grid place-items-center"><Loader2 size={20} className="animate-spin text-amber-400" /></div> : (
              <div className="max-h-56 overflow-y-auto divide-y divide-white/5 -mx-1" data-testid="activity-timeline">
                {data.timeline.length === 0 && <p className="p-4 text-center text-sm text-slate-500">No activity yet</p>}
                {data.timeline.map((e, i) => {
                  const I = Ic[e.type] || Gamepad2;
                  return (
                    <div key={i} className="px-1 py-2 flex items-center gap-3">
                      <div className={`shrink-0 grid place-items-center h-7 w-7 rounded-lg ${e.type === "deposit" ? "bg-emerald-500/15 text-emerald-300" : e.type === "withdraw" ? "bg-cyan-500/15 text-cyan-300" : "bg-white/5 text-slate-300"}`}><I size={14} /></div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold capitalize truncate">{GAME_LABELS[e.title] || e.title} {e.meta && <span className="text-[11px] text-slate-500 font-normal">· {e.meta}</span>}</p>
                        <p className="text-[10px] text-slate-500">{e.ts ? new Date(e.ts).toLocaleString() : ""} · <span className={stColor(e.status)}>{e.status}</span></p>
                      </div>
                      <span className={`font-mono-num font-bold text-sm ${e.type === "deposit" ? "text-emerald-400" : e.type === "withdraw" ? "text-cyan-400" : e.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {e.type === "deposit" ? "+" : e.type === "withdraw" ? "-" : e.amount >= 0 ? "+" : "-"}{formatCoins(Math.abs(e.amount))}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Delete */}
          <button onClick={doDelete} disabled={busy} data-testid={`delete-user-${uid}`}
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-rose-500/50 bg-rose-500/10 px-4 py-2.5 text-sm font-bold text-rose-300 hover:bg-rose-500/20">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete this user permanently
          </button>
        </div>
      </div>
    </div>
  );
}

function useConfig() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/config", config); setConfig(data); toast.success("Saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try { const { data } = await api.post("/admin/config/reset"); setConfig(data); toast.success("Reset to defaults"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return { config, setConfig, save, saving, reset };
}

function SaveBar({ save, saving, reset }) {
  return (
    <div className="flex gap-2 sticky bottom-20 md:bottom-4 z-20 w-fit rounded-2xl bg-[#070B1F]/85 backdrop-blur-md p-2 ring-1 ring-amber-500/20">
      <button onClick={save} disabled={saving} data-testid="admin-config-save" className="btn-gold rounded-xl px-6 py-2.5 flex items-center gap-2">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes
      </button>
      <button onClick={reset} data-testid="admin-config-reset" className="flex items-center gap-2 rounded-xl border border-slate-600 bg-black/40 px-4 py-2.5 text-sm text-slate-300"><RotateCcw size={15} /> Reset</button>
    </div>
  );
}

function GameToggle({ game, enabled, onToggle }) {
  return (
    <button onClick={onToggle} data-testid={`toggle-${game}`}
      className={`rounded-full px-3 py-1 text-xs font-bold ${enabled ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-rose-500/20 text-rose-300 border border-rose-500/40"}`}>
      {enabled ? "ON" : "OFF"}
    </button>
  );
}

function ColorForceControl() {
  const { t } = useLang();
  const [state, setState] = useState({ current_period: null, forced_number: null });
  const load = () => api.get("/admin/color/control").then(({ data }) => setState(data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, []);

  const force = async (n) => {
    try {
      const { data } = await api.post("/admin/color/force", { number: n });
      setState((s) => ({ ...s, forced_number: data.forced_number, current_period: data.current_period }));
      toast.success(n == null ? t("cleared") : `${t("forceNextResult")}: ${n}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/5 p-3" data-testid="color-force-control">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wider text-rose-300 flex items-center gap-1.5"><Zap size={13} /> {t("forceNextResult")}</p>
        <span className="text-[11px] text-slate-400">{t("period")}: <b className="font-mono-num text-amber-300">{state.current_period ?? "—"}</b></span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {Array.from({ length: 10 }).map((_, n) => (
          <button key={n} onClick={() => force(n)} data-testid={`color-force-${n}`}
            className={`grid place-items-center h-8 w-8 rounded-lg font-mono-num font-bold text-sm border transition-colors ${
              state.forced_number === n ? "btn-gold border-transparent" : "border-rose-500/30 bg-black/30 text-rose-100 hover:border-rose-400"
            }`}>{n}</button>
        ))}
        <button onClick={() => force(null)} data-testid="color-force-clear"
          className="ml-1 rounded-lg border border-slate-500/40 bg-black/30 px-3 h-8 text-xs text-slate-300 hover:border-slate-300">{t("cleared")}</button>
      </div>
      {state.forced_number != null && <p className="mt-2 text-[11px] text-amber-300">Next result forced → <b className="font-mono-num">{state.forced_number}</b></p>}
    </div>
  );
}


function GamesTab() {
  const { config, setConfig, save, saving, reset } = useConfig();
  if (!config) return <Loader2 className="animate-spin text-amber-400" />;
  const g = config.games;
  const setG = (game, patch) => setConfig({ ...config, games: { ...g, [game]: { ...g[game], ...patch } } });

  const spin = g.spin;
  const totalW = spin.segments.reduce((a, s) => a + Number(s.weight), 0) || 1;
  const winW = spin.segments.filter((s) => s.mult > 1).reduce((a, s) => a + Number(s.weight), 0);
  const jackW = spin.segments.filter((s) => s.mult >= 50).reduce((a, s) => a + Number(s.weight), 0);
  const tgl = (game) => <GameToggle game={game} enabled={g[game].enabled} onToggle={() => setG(game, { enabled: !g[game].enabled })} />;

  return (
    <div className="space-y-4">
      {/* Spin */}
      <div className="mw-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">Spin Wheel</h3>{tgl("spin")}
        </div>
        <div className="flex gap-4 text-xs mb-3">
          <span className="text-slate-400">Win rate: <b className="text-emerald-400 font-mono-num">{((winW / totalW) * 100).toFixed(1)}%</b></span>
          <span className="text-slate-400">Jackpot chance: <b className="text-amber-400 font-mono-num">{((jackW / totalW) * 100).toFixed(2)}%</b></span>
        </div>
        <div className="space-y-2">
          {spin.segments.map((seg, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className={`w-20 text-sm font-mono-num ${seg.mult >= 50 ? "gold-text font-bold" : "text-amber-200"}`}>{seg.label} ({seg.mult}x)</span>
              <span className="text-xs text-slate-500">weight</span>
              <NumInput value={seg.weight} step={0.1} min={0} testid={`spin-weight-${i}`} onChange={(v) => {
                const segs = spin.segments.map((x, j) => j === i ? { ...x, weight: v } : x);
                setG("spin", { segments: segs });
              }} />
            </div>
          ))}
        </div>
      </div>

      {/* Color */}
      <div className="mw-card p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">Color Prediction</h3>{tgl("color")}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {[["payout_color", "Color"], ["payout_color_special", "Color (0/5)"], ["payout_violet", "Violet"], ["payout_number", "Number"]].map(([k, l]) => (
            <label key={k} className="flex flex-col gap-1 text-xs text-slate-400">{l} payout (x)
              <NumInput value={g.color[k]} step={0.1} onChange={(v) => setG("color", { [k]: v })} cls="w-full" /></label>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mt-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">Round length (sec)
            <span className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-slate-200" data-testid="color-round-seconds">60 (fixed)</span></label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">Betting lock (sec)
            <NumInput value={g.color.lock_seconds ?? 10} min={5} onChange={(v) => setG("color", { lock_seconds: v })} cls="w-full" /></label>
        </div>
        <ColorRoundStats />
        <ColorForceControl />
      </div>

      {/* Dice */}
      <div className="mw-card p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">Dice</h3>{tgl("dice")}</div>
        <label className="flex flex-col gap-1 text-xs text-slate-400 max-w-[200px]">House edge factor (0–1, lower = more house profit)
          <NumInput value={g.dice.house_edge} step={0.01} min={0} onChange={(v) => setG("dice", { house_edge: v })} cls="w-full" /></label>
      </div>

      {/* Scratch */}
      <div className="mw-card p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">Scratch Card</h3>{tgl("scratch")}</div>
        <label className="flex flex-col gap-1 text-xs text-slate-400 max-w-[160px] mb-3">Cost per card
          <NumInput value={g.scratch.cost} onChange={(v) => setG("scratch", { cost: v })} cls="w-full" /></label>
        <div className="space-y-2">
          {g.scratch.prizes.map((p, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="w-20 text-sm font-mono-num text-amber-200">₹{p.prize}</span>
              <span className="text-xs text-slate-500">weight</span>
              <NumInput value={p.weight} step={0.1} min={0} onChange={(v) => {
                const prizes = g.scratch.prizes.map((x, j) => j === i ? { ...x, weight: v } : x);
                setG("scratch", { prizes });
              }} />
            </div>
          ))}
        </div>
      </div>

      <AppRulesCard config={config} setConfig={setConfig} />

      <SaveBar save={save} saving={saving} reset={reset} />
    </div>
  );
}

function RewardsTab() {
  const { config, setConfig, save, saving, reset } = useConfig();
  if (!config) return <Loader2 className="animate-spin text-amber-400" />;
  return (
    <div className="space-y-4">
      <RecurringBonusManager />
      <FestivalCard config={config} setConfig={setConfig} />
      <DailySpinCard config={config} setConfig={setConfig} />
      <FirstDepositCard config={config} setConfig={setConfig} />
      <VipLevelsCard config={config} setConfig={setConfig} />
      <div className="mw-card p-5">
        <h3 className="font-display font-bold mb-3">Daily Bonus</h3>
        <div className="flex flex-wrap gap-4">
          <label className="flex flex-col gap-1 text-xs text-slate-400">Base reward
            <NumInput value={config.bonus.base} onChange={(v) => setConfig({ ...config, bonus: { ...config.bonus, base: v } })} /></label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">Per streak day
            <NumInput value={config.bonus.per_streak} onChange={(v) => setConfig({ ...config, bonus: { ...config.bonus, per_streak: v } })} /></label>
        </div>
      </div>
      <div className="mw-card p-5">
        <h3 className="font-display font-bold mb-3">Mission Rewards</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(config.missions).map(([k, v]) => (
            <label key={k} className="flex flex-col gap-1 text-xs text-slate-400">{k}
              <NumInput value={v} onChange={(nv) => setConfig({ ...config, missions: { ...config.missions, [k]: nv } })} cls="w-full" /></label>
          ))}
        </div>
      </div>
      <div className="mw-card p-5">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><ArrowDownToLine size={18} className="text-cyan-400" /> Withdrawal Limits</h3>
        <div className="flex flex-wrap gap-4">
          <label className="flex flex-col gap-1 text-xs text-slate-400">Min withdrawal (₹)
            <NumInput value={config.withdraw.min} testid="wd-min" onChange={(v) => setConfig({ ...config, withdraw: { ...config.withdraw, min: v } })} /></label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">Daily limit per user
            <NumInput value={config.withdraw.daily_limit} testid="wd-daily" onChange={(v) => setConfig({ ...config, withdraw: { ...config.withdraw, daily_limit: v } })} /></label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">Flag if ≥ (₹)
            <NumInput value={config.withdraw.flag_threshold} testid="wd-flag" onChange={(v) => setConfig({ ...config, withdraw: { ...config.withdraw, flag_threshold: v } })} /></label>
        </div>
      </div>
      <SaveBar save={save} saving={saving} reset={reset} />
    </div>
  );
}

const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function RecurringBonusManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ amount: 50, freq: "weekly", day_of_week: 0, hour: 9, minute: 0, title: "", message: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/recurring-bonus").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!Number(form.amount)) { toast.error("Amount daalein"); return; }
    setBusy(true);
    try {
      await api.post("/admin/recurring-bonus", { ...form, amount: Number(form.amount), day_of_week: Number(form.day_of_week), hour: Number(form.hour), minute: Number(form.minute) });
      toast.success("Recurring bonus set");
      setForm({ amount: 50, freq: "weekly", day_of_week: 0, hour: 9, minute: 0, title: "", message: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggle = async (id) => { try { await api.put(`/admin/recurring-bonus/${id}`); load(); } catch (e) {} };
  const del = async (id) => { try { await api.delete(`/admin/recurring-bonus/${id}`); toast.success("Deleted"); load(); } catch (e) {} };
  const when = (r) => `${r.freq === "weekly" ? `Har ${DOW[r.day_of_week]}` : "Rozana"} ${String(r.hour).padStart(2, "0")}:${String(r.minute).padStart(2, "0")} IST`;

  return (
    <div className="mw-card p-5" data-testid="recurring-bonus">
      <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Repeat size={18} className="text-emerald-400" /> Recurring Bonus (sabhi players ko)</h3>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-slate-400">Amount (₹)
          <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="rb-amount"
            className="w-24 rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1.5 font-mono-num text-amber-200 focus:outline-none" /></label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">Frequency
          <select value={form.freq} onChange={(e) => setForm({ ...form, freq: e.target.value })} data-testid="rb-freq"
            className="rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1.5 text-sm focus:outline-none">
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
          </select></label>
        {form.freq === "weekly" && (
          <label className="flex flex-col gap-1 text-xs text-slate-400">Day
            <select value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: e.target.value })} data-testid="rb-day"
              className="rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1.5 text-sm focus:outline-none">
              {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select></label>
        )}
        <label className="flex flex-col gap-1 text-xs text-slate-400">Time (IST)
          <div className="flex items-center gap-1">
            <input type="number" min={0} max={23} value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} data-testid="rb-hour"
              className="w-14 rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1.5 font-mono-num focus:outline-none" />
            <span>:</span>
            <input type="number" min={0} max={59} value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} data-testid="rb-minute"
              className="w-14 rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1.5 font-mono-num focus:outline-none" />
          </div></label>
        <button onClick={create} disabled={busy} data-testid="rb-create" className="btn-gold rounded-xl px-4 py-2 text-sm font-bold flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Repeat size={14} />} Set
        </button>
      </div>
      <input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message (optional)" data-testid="rb-message"
        className="mt-3 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm focus:outline-none" />
      <div className="mt-3 space-y-2">
        {items.map((r) => (
          <div key={r.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 p-2.5 text-sm" data-testid={`rb-item-${r.id}`}>
            <Repeat size={14} className={r.active ? "text-emerald-400" : "text-slate-500"} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-emerald-200">₹{r.amount} · <span className="text-slate-300 font-normal">{when(r)}</span></p>
              {r.last_run_date && <p className="text-[10px] text-slate-500">last run: {r.last_run_date}</p>}
            </div>
            <button onClick={() => toggle(r.id)} data-testid={`rb-toggle-${r.id}`} className={`rounded-lg px-2.5 py-1 text-xs font-bold ${r.active ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border border-slate-600 text-slate-300"}`}>{r.active ? "ON" : "OFF"}</button>
            <button onClick={() => del(r.id)} data-testid={`rb-del-${r.id}`} className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-xs font-bold text-rose-300"><Trash2 size={12} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-slate-500">Koi recurring bonus set nahi hai</p>}
      </div>
    </div>
  );
}

function FraudCluster({ g, onBan }) {
  return (
    <div className="mw-card p-4" data-testid={`fraud-group-${g.ip}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <MapPin size={14} className="text-rose-400" />
        <span className="font-mono-num text-sm font-bold text-rose-300">{g.ip}</span>
        {g.match === "ip+device" && <span className="rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 px-2 py-0.5 text-[10px] font-bold text-fuchsia-300">{g.device}</span>}
        <span className="rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[11px] font-bold text-rose-300">{g.count} accounts</span>
        {g.match === "ip+device" && <span className="rounded-full border border-fuchsia-500/50 bg-fuchsia-500/15 px-2 py-0.5 text-[10px] font-bold text-fuchsia-200">PAKKA</span>}
        <span className="text-[10px] text-slate-500 ml-auto">last: {g.last_at ? timeAgo(g.last_at) : ""}</span>
      </div>
      <div className="space-y-1.5">
        {g.users.map((u) => (
          <div key={u.user_id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 p-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{u.name} {u.banned && <span className="text-[11px] text-rose-400">(banned)</span>}</p>
              <p className="text-[11px] text-slate-400 truncate">{u.email} · {formatCoins(u.coins)}</p>
            </div>
            {u.banned
              ? <button onClick={() => onBan(u.user_id, false)} data-testid={`fraud-unban-${u.user_id}`} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-300">Unban</button>
              : <button onClick={() => onBan(u.user_id, true)} data-testid={`fraud-ban-${u.user_id}`} className="flex items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-xs font-bold text-rose-300"><Ban size={12} /> Ban</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function FraudTab() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/admin/multi-accounts").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/fraud-settings").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const banUser = async (uid, banned) => {
    try { await api.post(`/admin/users/${uid}/ban`, { banned }); toast.success(banned ? "Banned" : "Unbanned"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveCfg = async (next) => {
    try { const { data } = await api.put("/admin/fraud-settings", next); setCfg(data); toast.success("Saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const strong = data?.strong || [];
  const byIp = data?.by_ip || [];
  const nothing = strong.length === 0 && byIp.length === 0;
  return (
    <div className="space-y-4" data-testid="fraud-tab">
      <div className="mw-card p-4 flex items-start gap-3">
        <ShieldAlert size={20} className="text-rose-400 mt-0.5 shrink-0" />
        <div>
          <p className="font-display font-bold">Multi-Account Detection</p>
          <p className="text-xs text-slate-400">Ek hi IP (aur same device) se bane 2+ accounts yahan flag hote hain. Same ghar/wifi ke log bhi dikh sakte hain — ban se pehle check karein.</p>
        </div>
      </div>

      {cfg && (
        <div className="mw-card p-4 flex flex-wrap items-center gap-3" data-testid="fraud-settings">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => saveCfg({ ...cfg, enabled: e.target.checked })} data-testid="fraud-enabled" className="accent-amber-400 h-4 w-4" />
            IP par <b>{cfg.threshold}+</b> accounts bane to turant admin ko alert bhejo
          </label>
          <span className="w-px h-5 bg-white/10" />
          <label className="flex items-center gap-2 text-xs text-slate-400">Threshold
            <input type="number" min={2} max={20} value={cfg.threshold} onChange={(e) => setCfg({ ...cfg, threshold: Number(e.target.value) })} data-testid="fraud-threshold"
              className="w-16 rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1 font-mono-num text-amber-200 focus:outline-none" /></label>
          <button onClick={() => saveCfg(cfg)} data-testid="fraud-save" className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-300">Save</button>
        </div>
      )}

      {!data ? <Loader2 className="animate-spin text-amber-400" /> : nothing ? (
        <div className="mw-card p-6 text-center text-sm text-slate-500" data-testid="fraud-empty">Koi multi-account flag nahi mila ✅</div>
      ) : (
        <>
          {strong.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-bold text-fuchsia-300 uppercase tracking-wider">Strong match (same IP + device)</p>
              {strong.map((g) => <FraudCluster key={`fp-${g.ip}-${g.device}`} g={g} onBan={banUser} />)}
            </div>
          )}
          {byIp.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-bold text-rose-300 uppercase tracking-wider">Same IP</p>
              {byIp.map((g) => <FraudCluster key={`ip-${g.ip}`} g={g} onBan={banUser} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ContentTab() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ type: "text", title: "", message: "", image_url: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/announcements").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/announcements/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/announcements", form);
      toast.success("Published to all players");
      setForm({ type: "text", title: "", message: "", image_url: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = async (a) => { await api.patch(`/admin/announcements/${a.id}`, { active: !a.active }); load(); };
  const remove = async (a) => { await api.delete(`/admin/announcements/${a.id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="mw-card p-5 space-y-4" data-testid="admin-content-form">
        <h3 className="font-display font-bold flex items-center gap-2"><Megaphone size={18} className="text-amber-400" /> New {t("announcement")}</h3>
        <div className="flex gap-2">
          {[["text", t("textMessage"), Type], ["banner", t("imageBanner"), ImageIcon]].map(([v, l, Ic]) => (
            <button key={v} onClick={() => setForm({ ...form, type: v })} data-testid={`content-type-${v}`}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold border transition-colors ${form.type === v ? "btn-gold" : "border-amber-500/25 bg-black/30 text-slate-300"}`}>
              <Ic size={15} /> {l}
            </button>
          ))}
        </div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={`${t("title")} (optional)`}
          data-testid="content-title" className="w-full rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
        {form.type === "text" ? (
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("message")} rows={3}
            data-testid="content-message" className="w-full rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
        ) : (
          <div className="flex items-center gap-3">
            <button onClick={() => fileRef.current?.click()} data-testid="content-upload-btn" className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-black/30 px-4 py-2 text-sm font-bold text-cyan-300">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {t("uploadImage")}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="content-image-input" />
            {form.image_url && <img src={form.image_url} alt="" className="h-12 rounded-lg border border-white/10" />}
          </div>
        )}
        <button onClick={create} disabled={busy} data-testid="content-create-btn" className="btn-gold rounded-xl px-6 py-2.5 flex items-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {t("create")}
        </button>
      </div>

      <div className="space-y-2" data-testid="admin-announcements-list">
        {list.map((a) => (
          <div key={a.id} className="mw-card p-4 flex items-center gap-3">
            {a.type === "banner"
              ? <img src={avatarUrl(a.image_url)} alt="" className="h-12 w-20 rounded-lg object-cover border border-white/10" />
              : <div className="grid place-items-center h-12 w-12 rounded-lg bg-black/40 text-amber-300"><Type size={18} /></div>}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{a.title || (a.type === "banner" ? "Banner" : a.message)}</p>
              {a.title && a.message && <p className="text-xs text-slate-400 truncate">{a.message}</p>}
            </div>
            <button onClick={() => toggle(a)} data-testid={`announcement-toggle-${a.id}`} className={`rounded-full px-3 py-1 text-xs font-bold ${a.active ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-600/30 text-slate-400 border border-slate-600"}`}>
              {a.active ? "LIVE" : "OFF"}
            </button>
            <button onClick={() => remove(a)} data-testid={`announcement-delete-${a.id}`} className="grid place-items-center h-8 w-8 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300"><Trash2 size={14} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-slate-500 text-sm">{t("noAnnouncements")}</p>}
      </div>
    </div>
  );
}

function WithdrawalsTab() {
  const [rows, setRows] = useState([]);
  const [deps, setDeps] = useState([]);
  const load = () => {
    api.get("/admin/withdrawals").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/admin/deposits").then(({ data }) => setDeps(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const act = async (w, action) => {
    try { await api.post(`/admin/withdrawals/${w.id}/action`, { action }); toast.success(action === "approve" ? "Approved" : "Rejected & refunded"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const actDep = async (d, action) => {
    try { await api.post(`/admin/deposits/${d.id}/action`, { action }); toast.success(action === "approve" ? "Balance added ✅" : "Rejected"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-withdrawals">
      <div>
        <h3 className="font-display font-bold mb-2">Deposit Requests</h3>
        <div className="space-y-2" data-testid="admin-deposits">
          {deps.map((d) => (
            <div key={d.id} className="mw-card p-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{d.user_name || d.user_id} {d.reference && <span className="text-xs text-slate-400">· ref: {d.reference}</span>}</p>
                <p className="text-xs text-slate-400">{new Date(d.created_at).toLocaleString()}</p>
              </div>
              <span className="font-mono-num font-bold text-emerald-300">₹{d.amount}</span>
              {d.screenshot_url && (
                <a href={avatarUrl(d.screenshot_url)} target="_blank" rel="noreferrer" data-testid={`deposit-proof-${d.id}`} title="View payment proof">
                  <img src={avatarUrl(d.screenshot_url)} alt="proof" className="h-12 w-12 rounded-lg object-cover border border-amber-500/40 hover:border-amber-400" />
                </a>
              )}
              {d.status === "pending" ? (
                <div className="flex gap-2">
                  <button onClick={() => actDep(d, "approve")} data-testid={`deposit-approve-${d.id}`} className="flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-300"><Check size={13} /> Approve</button>
                  <button onClick={() => actDep(d, "reject")} data-testid={`deposit-reject-${d.id}`} className="flex items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-xs font-bold text-rose-300"><X size={13} /> Reject</button>
                </div>
              ) : (
                <span className={`text-xs font-bold ${d.status === "approved" ? "text-emerald-400" : "text-rose-400"}`}>{d.status}</span>
              )}
            </div>
          ))}
          {deps.length === 0 && <p className="text-slate-500 text-sm">No deposit requests</p>}
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold mb-2">Withdrawal Requests</h3>
        <div className="space-y-2">
          {rows.map((w) => (
            <div key={w.id} className="mw-card p-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{w.user_name} <span className="text-xs text-slate-400">· {w.method} · {w.account}</span> {w.flagged && <span className="inline-flex items-center gap-0.5 text-xs text-amber-400"><AlertTriangle size={12} /> flagged</span>}</p>
                <p className="text-xs text-slate-400">{new Date(w.created_at).toLocaleString()}</p>
              </div>
              <span className="font-mono-num font-bold text-amber-200">₹{w.coins}</span>
              {w.status === "pending" ? (
                <div className="flex gap-2">
                  <button onClick={() => act(w, "approve")} data-testid={`withdraw-approve-${w.id}`} className="flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-300"><Check size={13} /> Approve</button>
                  <button onClick={() => act(w, "reject")} data-testid={`withdraw-reject-${w.id}`} className="flex items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-xs font-bold text-rose-300"><X size={13} /> Reject</button>
                </div>
              ) : (
                <span className={`text-xs font-bold ${w.status === "approved" ? "text-emerald-400" : "text-rose-400"}`}>{w.status}</span>
              )}
            </div>
          ))}
          {rows.length === 0 && <p className="text-slate-500 text-sm">No withdrawal requests</p>}
        </div>
      </div>
    </div>
  );
}


function PayInfoTab() {
  const { t } = useLang();
  const [info, setInfo] = useState({ upi_id: "", qr_url: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/payment-info").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/payment-info", { upi_id: info.upi_id || "", note: info.note || "" });
      setInfo((i) => ({ ...i, ...data }));
      toast.success("Saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadQr = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/payment-info/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInfo((i) => ({ ...i, qr_url: data.qr_url }));
      toast.success("QR uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-payinfo">
      <div className="mw-card p-5 space-y-4">
        <h3 className="font-display font-bold flex items-center gap-2"><CreditCard size={18} className="text-emerald-400" /> {t("payInfo")}</h3>
        <p className="text-xs text-slate-400">Players will pay to this UPI ID / QR when they request a deposit.</p>
        <div>
          <label className="text-xs text-slate-400">{t("upiId")}</label>
          <input value={info.upi_id || ""} onChange={(e) => setInfo({ ...info, upi_id: e.target.value })} placeholder="yourname@upi"
            data-testid="payinfo-upi" className="w-full mt-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Note / Instructions (optional)</label>
          <textarea value={info.note || ""} onChange={(e) => setInfo({ ...info, note: e.target.value })} rows={2} placeholder="e.g. Add your name in payment remarks"
            data-testid="payinfo-note" className="w-full mt-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={() => fileRef.current?.click()} data-testid="payinfo-qr-btn" className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-black/30 px-4 py-2 text-sm font-bold text-cyan-300">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <QrCode size={15} />} Upload QR
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={uploadQr} className="hidden" data-testid="payinfo-qr-input" />
          {info.qr_url && <img src={avatarUrl(info.qr_url)} alt="QR" className="h-20 w-20 rounded-lg bg-white p-1 object-contain border border-white/10" data-testid="payinfo-qr-preview" />}
        </div>
        <button onClick={save} disabled={busy} data-testid="payinfo-save" className="btn-gold rounded-xl px-6 py-2.5 flex items-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>

      <div className="mw-card p-5" data-testid="payinfo-preview">
        <h3 className="font-display font-bold flex items-center gap-2 mb-1"><QrCode size={18} className="text-cyan-400" /> Player ko aisa dikhega (Add Money)</h3>
        <p className="text-xs text-slate-400 mb-3">Game khelne wale jab paisa add karenge to unhe yahi UPI + QR dikhega.</p>
        {(info.qr_url || info.upi_id) ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-col sm:flex-row items-center gap-4">
            <div className="bg-white p-2 rounded-lg shrink-0">
              {info.qr_url
                ? <img src={avatarUrl(info.qr_url)} alt="QR" className="h-32 w-32 object-contain" data-testid="payinfo-preview-qr" />
                : <QRCodeCanvas value={`upi://pay?pa=${encodeURIComponent(info.upi_id)}&pn=BamBamBhole&cu=INR`} size={128} data-testid="payinfo-preview-qr-auto" />}
            </div>
            <div className="text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Pay to UPI</p>
              <p className="font-mono-num text-lg font-bold text-emerald-300 break-all">{info.upi_id || "—"}</p>
              {info.note && <p className="text-xs text-slate-400 mt-1">{info.note}</p>}
              <p className="text-[10px] text-slate-500 mt-2">{info.qr_url ? "Aapka upload kiya hua QR" : "UPI ID se auto-QR"}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-amber-300">Pehle upar UPI ID daalein (ya QR upload karein) — fir yahan preview dikhega.</p>
        )}
      </div>

      <PaymentMethodsManager />
    </div>
  );
}


function PaymentMethodsManager() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [upi, setUpi] = useState("");
  const [note, setNote] = useState("");
  const refs = useRef({});
  const load = () => api.get("/admin/payment-methods").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!upi.trim()) return;
    try { await api.post("/admin/payment-methods", { upi_id: upi, note }); setUpi(""); setNote(""); load(); toast.success("Added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (m) => { await api.put(`/admin/payment-methods/${m.id}`, { active: !m.active }); load(); };
  const del = async (m) => { await api.delete(`/admin/payment-methods/${m.id}`); load(); toast.success("Deleted"); };
  const upQr = async (m, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/payment-methods/${m.id}/qr`, fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); toast.success("QR uploaded"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mw-card p-5 space-y-4" data-testid="admin-payment-methods">
      <h3 className="font-display font-bold flex items-center gap-2"><CreditCard size={18} className="text-amber-400" /> {t("paymentMethods")}</h3>
      <p className="text-xs text-slate-400">Multiple UPI/QR daal sakte hain — players ko baari-baari (load balance) ye dikhenge.</p>
      <div className="flex flex-wrap gap-2">
        <input value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="name@upi" data-testid="pm-upi-input" className="flex-1 min-w-[160px] rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="note (optional)" data-testid="pm-note-input" className="flex-1 min-w-[140px] rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none" />
        <button onClick={add} data-testid="pm-add-btn" className="btn-gold rounded-lg px-4 py-2 text-sm font-bold">+ {t("addMethod")}</button>
      </div>
      <div className="space-y-2">
        {list.map((m) => (
          <div key={m.id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`pm-row-${m.id}`}>
            {m.qr_url
              ? <img src={avatarUrl(m.qr_url)} alt="qr" className="h-12 w-12 rounded bg-white p-0.5 object-contain" />
              : <div className="h-12 w-12 rounded grid place-items-center border border-dashed border-slate-600 text-slate-600"><QrCode size={16} /></div>}
            <div className="flex-1 min-w-0">
              <p className="font-mono-num text-sm font-bold text-emerald-300 truncate">{m.upi_id}</p>
              {m.note && <p className="text-[11px] text-slate-400 truncate">{m.note}</p>}
            </div>
            <input ref={(el) => (refs.current[m.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upQr(m, f); }} data-testid={`pm-qr-input-${m.id}`} />
            <button onClick={() => refs.current[m.id]?.click()} data-testid={`pm-qr-btn-${m.id}`} className="rounded-lg border border-cyan-400/40 bg-black/30 px-2 py-1 text-xs text-cyan-300">QR</button>
            <button onClick={() => toggle(m)} data-testid={`pm-toggle-${m.id}`} className={`rounded-lg border px-2 py-1 text-xs font-bold ${m.active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-600 text-slate-400"}`}>{m.active ? t("active") : t("inactive")}</button>
            <button onClick={() => del(m)} data-testid={`pm-del-${m.id}`} className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-xs text-rose-300"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-slate-500">Koi method nahi — upar add karein (warna single UPI/QR fallback chalega)</p>}
      </div>
    </div>
  );
}

function PromoTab() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ code: "", amount: 100, max_uses: "" });
  const load = () => api.get("/admin/promos").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/promos", { code: form.code, amount: Number(form.amount), max_uses: form.max_uses ? Number(form.max_uses) : null }); setForm({ code: "", amount: 100, max_uses: "" }); load(); toast.success("Created"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (p) => { await api.put(`/admin/promos/${p.code}`); load(); };
  const del = async (p) => { await api.delete(`/admin/promos/${p.code}`); load(); toast.success("Deleted"); };
  return (
    <div className="space-y-4" data-testid="admin-promos">
      <div className="mw-card p-5 space-y-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Gift size={18} className="text-fuchsia-400" /> {t("createPromo")}</h3>
        <div className="flex flex-wrap gap-2">
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CODE" data-testid="promo-code-input" className="rounded-lg border border-fuchsia-400/30 bg-black/40 px-3 py-2 text-sm font-mono-num tracking-wider focus:outline-none w-40" />
          <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="₹ amount" data-testid="promo-amount-input" className="rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm w-28 focus:outline-none" />
          <input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder={t("maxUses")} data-testid="promo-maxuses-input" className="rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm w-28 focus:outline-none" />
          <button onClick={create} data-testid="promo-create-btn" className="btn-gold rounded-lg px-4 py-2 text-sm font-bold">Create</button>
        </div>
      </div>
      <div className="space-y-2">
        {list.map((p) => (
          <div key={p.code} className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/20 px-4 py-3" data-testid={`promo-row-${p.code}`}>
            <div className="flex-1">
              <p className="font-mono-num font-bold text-fuchsia-300">{p.code}</p>
              <p className="text-[11px] text-slate-400">₹{p.amount} · used {p.used_count || 0}{p.max_uses ? `/${p.max_uses}` : ""}</p>
            </div>
            <button onClick={() => toggle(p)} data-testid={`promo-toggle-${p.code}`} className={`rounded-lg border px-2 py-1 text-xs font-bold ${p.active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-600 text-slate-400"}`}>{p.active ? t("active") : t("inactive")}</button>
            <button onClick={() => del(p)} data-testid={`promo-del-${p.code}`} className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-xs text-rose-300"><Trash2 size={13} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-slate-500 text-sm">No promo codes yet</p>}
      </div>
    </div>
  );
}


function KycTab() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const load = () => api.get(`/admin/kyc?status=${status}`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [status]);
  const act = async (u, action) => {
    try { await api.post(`/admin/kyc/${u.user_id}/action`, { action }); toast.success(action === "approve" ? "KYC approved ✅" : "KYC rejected"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const DOC_LABELS = { aadhaar: "Aadhaar", pan: "PAN", selfie: "Selfie", bank: "Bank" };
  return (
    <div className="space-y-4" data-testid="admin-kyc">
      <div className="flex gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`kyc-filter-${s}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold capitalize border ${status === s ? "btn-gold" : "border-amber-500/30 bg-black/30 text-amber-200"}`}>{s}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map((u) => {
          const docs = (u.kyc || {}).docs || {};
          return (
            <div key={u.user_id} className="mw-card p-4" data-testid={`kyc-user-${u.user_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </div>
                {(u.kyc || {}).status === "pending" ? (
                  <div className="flex gap-2">
                    <button onClick={() => act(u, "approve")} data-testid={`kyc-approve-${u.user_id}`} className="flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-300"><Check size={13} /> Approve</button>
                    <button onClick={() => act(u, "reject")} data-testid={`kyc-reject-${u.user_id}`} className="flex items-center gap-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-xs font-bold text-rose-300"><X size={13} /> Reject</button>
                  </div>
                ) : (
                  <span className={`text-xs font-bold ${(u.kyc || {}).status === "approved" ? "text-emerald-400" : "text-rose-400"}`}>{(u.kyc || {}).status}</span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-3">
                {["aadhaar", "pan", "selfie", "bank"].map((d) => (
                  <div key={d} className="text-center">
                    <p className="text-[10px] uppercase text-slate-400 mb-1">{DOC_LABELS[d]}</p>
                    {docs[d] ? (
                      <a href={avatarUrl(docs[d])} target="_blank" rel="noreferrer" data-testid={`kyc-doc-${d}-${u.user_id}`}>
                        <img src={avatarUrl(docs[d])} alt={d} className="h-20 w-28 rounded-lg object-cover border border-amber-500/40 hover:border-amber-400" />
                      </a>
                    ) : <div className="h-20 w-28 rounded-lg border border-dashed border-slate-600 grid place-items-center text-slate-600 text-xs">—</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-slate-500 text-sm">No {status} KYC requests</p>}
      </div>
    </div>
  );
}


function SupportTab() {
  const { t } = useLang();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const QUICK_REPLIES = [
    "नमस्ते! हम आपकी कैसे मदद कर सकते हैं?",
    "आपका बैलेंस जोड़ दिया गया है ✅",
    "आपका withdraw 24 घंटे में process हो जाएगा ⏳",
    "आपकी डिपॉज़िट रिक्वेस्ट अप्रूव हो गई है 🎁",
    "थोड़ी देर में टीम आपसे संपर्क करेगी 🙏",
  ];

  const loadThreads = () => api.get("/admin/support/threads").then(({ data }) => setThreads(data)).catch(() => {});
  const loadMessages = (uid) => api.get(`/admin/support/${uid}/messages`).then(({ data }) => setMessages(data)).catch(() => {});

  useEffect(() => { loadThreads(); const id = setInterval(loadThreads, 6000); return () => clearInterval(id); }, []);
  useEffect(() => {
    if (!active) return;
    loadMessages(active.user_id);
    const id = setInterval(() => loadMessages(active.user_id), 5000);
    return () => clearInterval(id);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const open = (th) => { setActive(th); setMessages([]); loadThreads(); };

  const sendText = async (msg) => {
    msg = (msg || "").trim();
    if (!msg || !active || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/support/${active.user_id}/messages`, { text: msg });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reply = () => sendText(text);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !active || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/support/${active.user_id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => [...m, data]);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4" data-testid="admin-support">
      <div className="space-y-2 max-h-[62vh] overflow-y-auto">
        {threads.map((th) => (
          <button key={th.user_id} onClick={() => open(th)} data-testid={`support-thread-${th.user_id}`}
            className={`w-full text-left mw-card p-3 transition-colors ${active?.user_id === th.user_id ? "ring-1 ring-amber-400" : ""}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold truncate">{th.user_name}</span>
              {th.unread > 0 && <span className="grid place-items-center h-5 min-w-5 px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white">{th.unread}</span>}
            </div>
            <p className="text-xs text-slate-400 truncate mt-0.5">{th.last_text}</p>
          </button>
        ))}
        {threads.length === 0 && <p className="text-slate-500 text-sm">{t("noChats")}</p>}
      </div>

      <div className="mw-card p-4 flex flex-col h-[62vh]">
        {!active ? (
          <div className="h-full grid place-items-center text-slate-500 text-sm"><div className="text-center"><Headset size={30} className="mx-auto mb-2 text-slate-600" />{t("supportInbox")}</div></div>
        ) : (
          <>
            <div className="border-b border-white/10 pb-2 mb-2 font-display font-bold">{active.user_name}</div>
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.sender}`}>
                  <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${m.sender === "admin" ? "bg-amber-500/90 text-black rounded-br-sm" : "border border-cyan-400/30 bg-cyan-500/10 text-cyan-50 rounded-bl-sm"}`}>
                    {m.image_url && <img src={m.image_url} alt="image" className="mb-1 max-h-48 min-h-[60px] min-w-[80px] rounded-lg bg-black/20 object-contain" data-testid="admin-msg-image" />}
                    {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                    <p className="text-[10px] opacity-60 mt-1 text-right">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="mt-3 border-t border-white/10 pt-3 space-y-2">
              <div className="flex flex-wrap gap-1.5" data-testid="quick-replies">
                {QUICK_REPLIES.map((q, i) => (
                  <button key={i} onClick={() => sendText(q)} disabled={busy} data-testid={`quick-reply-${i}`}
                    className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-1 text-[11px] text-cyan-100 hover:border-cyan-400/70 transition-colors">
                    {q.length > 26 ? q.slice(0, 24) + "…" : q}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="admin-support-image-input" />
                <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="admin-support-image-btn"
                  className="grid place-items-center h-11 w-11 rounded-xl border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 shrink-0 hover:border-cyan-400 transition-colors">
                  <ImagePlus size={18} />
                </button>
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()}
                  placeholder={t("typeMessage")} data-testid="admin-support-input"
                  className="flex-1 rounded-xl border border-amber-500/30 bg-black/40 px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500/80" />
                <button onClick={reply} disabled={busy} data-testid="admin-support-send" className="grid place-items-center h-11 w-11 rounded-xl btn-gold shrink-0">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function LogsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/logs").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="admin-logs">
      {rows.map((r) => (
        <div key={r.id} className="mw-card p-3 flex items-center gap-3 text-sm flex-wrap">
          <span className="font-mono-num text-[11px] text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
          <span className="rounded px-2 py-0.5 text-xs font-bold bg-amber-500/15 text-amber-300">{r.action}</span>
          <span className="text-slate-300 flex-1 min-w-0">{r.detail}</span>
          <span className="text-xs text-slate-500">{r.admin}</span>
        </div>
      ))}
      {rows.length === 0 && <p className="text-slate-500 text-sm">No admin activity yet</p>}
    </div>
  );
}

